import { Loader, Text, useComputedColorScheme } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import type { TestRunTraceIdentifier } from '~/server/api/routers/trace';
import type { TraceAvailability } from '~/common/playwright-test/trace-config';
import { getTraceUrl } from '~/common/trace/getTraceUrl';
import { api } from '~/trpc-extra/api';

// The classic reporter records the terminal attempt status BEFORE it awaits the trace
// scrub+upload (see test-reporter.ts: "Run transaction first, then upload sequentially"),
// so "attempt finished" does not imply "trace object exists yet". Keep polling for a grace
// period before declaring the trace missing.
const TRACE_UPLOAD_POLL_INTERVAL_MS = 5_000;
const TRACE_UPLOAD_GRACE_PERIOD_MS = 90_000;

function TraceEmptyState({ className, title, body }: { className?: string; title: string; body: React.ReactNode }) {
  return (
    <div className={className}>
      <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gray-100 dark:bg-gray-800">
        <Text fw={500}>{title}</Text>
        <Text
          size="sm"
          c="dimmed"
          className="max-w-md text-center"
        >
          {body}
        </Text>
      </div>
    </div>
  );
}

export default function PlaywrightTraceViewer({
  className,
  trace,
  ready,
  showTitle = false,
  traceAvailability = 'unknown',
}: {
  className?: string;
  showTitle?: boolean;
  ready: boolean;
  trace:
    | {
        type: 'identifier';
        traceIdentifier: TestRunTraceIdentifier;
      }
    | {
        type: 'zipUrl';
        traceZipUrl: string;
      };
  /**
   * Whether the run's Playwright config had tracing enabled, from
   * getTraceAvailabilityFromProjectSettings. Drives the empty-state copy: only 'disabled'
   * may blame the customer's config. Leave as 'unknown' for classic no-code runs — those
   * have no customer Playwright config, so config advice would be misleading there.
   */
  traceAvailability?: TraceAvailability;
}) {
  const colorScheme = useComputedColorScheme();

  // Only the trace object may still be arriving for identifier traces (we poll storage
  // directly); a zipUrl comes from an already-recorded artifact row, so absence is final
  // as far as this component can tell.
  const canStillArrive = trace.type === 'identifier' && traceAvailability !== 'disabled';
  const [uploadGraceExpired, setUploadGraceExpired] = useState(false);

  // Only fetch presigned URL for type 1 (classic no-code test viewer)
  const { data: presignedData, isError } = api.trace.getPresignedUrl.useQuery(
    { traceIdentifier: trace.type === 'identifier' ? trace.traceIdentifier : undefined! },
    {
      enabled: ready && trace.type === 'identifier',
      refetchOnWindowFocus: false,
      // Poll only while the object is confirmed absent and may still be uploading.
      refetchInterval: (query) => {
        if (!canStillArrive || uploadGraceExpired) {
          return false;
        }
        const { data } = query.state;
        if (data === undefined || data.presignedUrl) {
          return false;
        }
        return TRACE_UPLOAD_POLL_INTERVAL_MS;
      },
    },
  );

  // Use presigned URL for type 1, direct URL for type 2
  const effectiveTraceUrl = trace.type === 'identifier' ? presignedData?.presignedUrl : trace.traceZipUrl;

  const traceViewerUrl =
    effectiveTraceUrl ?
      getTraceUrl(
        { traceZipUrl: effectiveTraceUrl },
        { theme: colorScheme === 'light' ? 'light-mode' : 'dark-mode', showTitle },
      )
    : undefined;

  // No trace URL for a finished attempt. This is either permanent (tracing disabled, trace
  // not retained, upload failed) or transient (trace still scrubbing/uploading) — the grace
  // period below decides which one we present.
  const traceAbsent =
    ready &&
    ((trace.type === 'zipUrl' && !trace.traceZipUrl) ||
      (trace.type === 'identifier' && presignedData !== undefined && !presignedData.presignedUrl));

  const traceKey = trace.type === 'identifier' ? JSON.stringify(trace.traceIdentifier) : trace.traceZipUrl;
  useEffect(() => {
    setUploadGraceExpired(false);
  }, [traceKey]);

  useEffect(() => {
    if (!(traceAbsent && canStillArrive)) {
      return;
    }
    const timer = setTimeout(() => setUploadGraceExpired(true), TRACE_UPLOAD_GRACE_PERIOD_MS);
    return () => clearTimeout(timer);
  }, [traceAbsent, canStillArrive]);

  if (trace.type === 'identifier' && isError) {
    return (
      <TraceEmptyState
        className={className}
        title="Couldn't load the trace"
        body="Something went wrong while checking for this run's trace. Refresh the page to try again."
      />
    );
  }

  const traceMissing = traceAbsent && (!canStillArrive || uploadGraceExpired);

  if (traceMissing) {
    if (traceAvailability === 'disabled') {
      return (
        <TraceEmptyState
          className={className}
          title="No trace was recorded for this run"
          body={
            <>
              Tracing is disabled in your Playwright config, so no trace was captured. Set <code>use.trace</code> (for
              example <code>&apos;retain-on-failure&apos;</code>) to capture traces for future runs.
            </>
          }
        />
      );
    }
    if (traceAvailability === 'enabled') {
      return (
        <TraceEmptyState
          className={className}
          title="No trace is available for this run"
          body={
            <>
              Tracing is enabled in your Playwright config, but no trace was kept for this attempt — for example,{' '}
              <code>&apos;retain-on-failure&apos;</code> only keeps traces when the attempt fails.
            </>
          }
        />
      );
    }
    return (
      <TraceEmptyState
        className={className}
        title="No trace is available for this run"
        body="If the run finished recently, the trace may still be uploading — refresh the page to check again."
      />
    );
  }

  return (
    <div className={className}>
      {(ready || trace.type === 'zipUrl') && traceViewerUrl ?
        <iframe
          width="100%"
          height="100%"
          src={traceViewerUrl}
        />
      : <div className="h-full w-full bg-gray-100 dark:bg-gray-800">
          <div className="flex h-full w-full flex-col items-center justify-center space-y-4">
            <Loader />
            <span className="text-sm text-gray-500">Trace is not yet available...</span>
          </div>
        </div>
      }
    </div>
  );
}
